import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import BASE_URL from "../../BASEURL";

export default function EditAbout() {
  const navigate = useNavigate();

  const [data, setData] = useState({
    hero: {
      title: "",
      description1: "",
      description2: "",
      description3: "",
      // `image` will hold either a File (when user selects) or a string URL from API
      image: null,
    }
  });

  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [resourceId, setResourceId] = useState(null);

  useEffect(() => {
    // Fetch existing data from API and populate the form
    const fetchData = async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/as`);
        if (!res.ok) return;
        const json = await res.json();
        // API returns { success, data: [ { ... } ] } — prefer the first item if present
        const payload = (json && Array.isArray(json.data) && json.data.length) ? json.data[0] : json;
        // map API fields to local state (support underscore names)
        const hero = {
          title: payload.title || payload.hero?.title || '',
          description1: payload.description_1 || payload.description1 || payload.hero?.description1 || '',
          description2: payload.description_2 || payload.description2 || payload.hero?.description2 || '',
          description3: payload.description_3 || payload.description3 || payload.hero?.description3 || '',
          image: payload.image || payload.hero?.image || null,
        };
        setData({ hero });
        if (hero.image) setPreview(hero.image);
        setResourceId(payload._id || payload.id || null);
      } catch (err) {
        // silent fail — you can show an error toast here
        console.error('Failed to fetch about hero:', err);
      }
    };

    fetchData();
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPreview(URL.createObjectURL(file));
      setData(prev => ({ ...prev, hero: { ...prev.hero, image: file } }));
    }
  };

  

  return (
    <div className="max-w-6xl mx-auto md:p-6 space-y-10">

      {/* HEADER */}
      <div className="flex justify-between items-start md:items-center gap-3 flex-col md:flex-row">
        <h1 className="text-2xl font-bold text-primaryDark">Edit About Page</h1>
        <button
          onClick={() => navigate("/admin/aboutcms")}
          className="bg-primary text-white px-6 py-2 rounded hover:bg-primaryDark"
        >
          Back
        </button>
      </div>

      {/* HERO SECTION */}
      <section className="bg-white p-6 rounded shadow">
        <h2 className="font-semibold mb-4">About Section</h2>

        <input
          className="input mb-3 border border-primaryDark/30 p-1 w-full"
          placeholder="Title"
          value={data.hero.title}
          onChange={(e) =>
            setData({ ...data, hero: { ...data.hero, title: e.target.value } })
          }
        />

        <textarea
          className="input mb-3 border border-primaryDark/30 p-1 w-full"
          placeholder="Description 1"
          value={data.hero.description1}
          onChange={(e) =>
            setData({ ...data, hero: { ...data.hero, description1: e.target.value } })
          }
        />

        <textarea
          className="input mb-3 border border-primaryDark/30 p-1 w-full"
          placeholder="Description 2"
          value={data.hero.description2}
          onChange={(e) =>
            setData({ ...data, hero: { ...data.hero, description2: e.target.value } })
          }
        />

        <textarea
          className="input mb-3 border border-primaryDark/30 p-1 w-full"
          placeholder="Description 3"
          value={data.hero.description3}
          onChange={(e) =>
            setData({ ...data, hero: { ...data.hero, description3: e.target.value } })
          }
        />

        <div className="space-y-3">
      <input
        type="file"
        className="w-full md:w-[250px]"
        accept="image/*"
        onChange={handleImageChange}
      />

      {preview && (
        <img
          src={preview}
          alt="Preview"
          className="mt-3 h-40 w-auto rounded-lg border object-cover"
        />
      )}
    </div>
      </section>

      <div className="flex gap-3">
        <button
          onClick={() => navigate('/admin/aboutcms')}
          className="bg-gray-200 text-gray-800 px-6 py-3 rounded"
          disabled={saving}
        >
          Cancel
        </button>

        <button
          onClick={async () => {
            setSaving(true);
            try {
              // If image is a File, send multipart/form-data
              const hero = data.hero || {};
              let res;
              const url = resourceId ? `${BASE_URL}/api/as/${resourceId}` : `${BASE_URL}/api/as`;
              if (hero.image instanceof File) {
                const form = new FormData();
                form.append('title', hero.title || '');
                form.append('description_1', hero.description1 || '');
                form.append('description_2', hero.description2 || '');
                form.append('description_3', hero.description3 || '');
                form.append('image', hero.image);
                res = await fetch(url, {
                  method: 'PUT',
                  body: form,
                });
              } else {
                // send JSON — backend should accept these fields
                const payload = {
                  title: hero.title || '',
                  description_1: hero.description1 || '',
                  description_2: hero.description2 || '',
                  description_3: hero.description3 || '',
                  image: hero.image || null,
                };
                res = await fetch(url, {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(payload),
                });
              }

              if (!res.ok) {
                const txt = await res.text();
                throw new Error(txt || 'Save failed');
              }

              // Optionally refresh or navigate back
              navigate('/admin/aboutcms');
            } catch (err) {
              console.error('Save error', err);
              alert('Failed to save changes');
            } finally {
              setSaving(false);
            }
          }}
          className="bg-primary text-white px-6 py-3 rounded hover:bg-primaryDark"
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save All Changes'}
        </button>
      </div>
    </div>
  );
}
